'use client';

import { c as _c } from "react/compiler-runtime";
import { type PointerEvent, useCallback, useEffect, useRef } from 'react';
import { useMediaQuery } from '@/app/hooks/useMediaQuery';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const FRAME_DURATION = 1000 / 60;
const BLOB_TRAVEL_X = 46;
const POINTER_FOLLOW = 0.18;
const SETTLE_THRESHOLD = 0.001;
interface UsePointerGlowOptions {
  isEnabled: boolean;
  resetKey?: string;
}
const clamp = (value: number) => Math.max(-1, Math.min(1, value));
const getPointerX = (event: PointerEvent<HTMLElement>): number | null => {
  const bounds = event.currentTarget.getBoundingClientRect();
  if (!bounds.width) return null;
  return clamp((event.clientX - bounds.left) / bounds.width * 2 - 1);
};
const applyPosition = (indicator: HTMLSpanElement | null, position: number) => {
  indicator?.style.setProperty('--spartan-tabs-blob-x', `${position * BLOB_TRAVEL_X}%`);
};
export const usePointerGlow = t0 => {
  const $ = _c(19);
  const {
    isEnabled,
    resetKey
  } = t0;
  const indicatorRef = useRef(null);
  const frameRef = useRef(null);
  const timestampRef = useRef(null);
  const currentRef = useRef(0);
  const targetRef = useRef(0);
  const shouldReduceMotion = useMediaQuery(REDUCED_MOTION_QUERY, false) ?? false;
  const isActive = isEnabled && !shouldReduceMotion;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (frameRef.current === null) {
        return;
      }
      window.cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const cancelFrame = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      cancelFrame();
      timestampRef.current = null;
      currentRef.current = 0;
      targetRef.current = 0;
      applyPosition(indicatorRef.current, 0);
      indicatorRef.current?.removeAttribute("data-pointer-glow-active");
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const resetMotion = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      if (frameRef.current !== null) {
        return;
      }
      const animate = timestamp => {
        const elapsed = timestampRef.current === null ? FRAME_DURATION : timestamp - timestampRef.current;
        const timeScale = Math.min(2, elapsed / FRAME_DURATION);
        const follow = 1 - (1 - POINTER_FOLLOW) ** timeScale;
        const next = currentRef.current + (targetRef.current - currentRef.current) * follow;
        const isSettled = Math.abs(targetRef.current - next) <= SETTLE_THRESHOLD;
        timestampRef.current = timestamp;
        currentRef.current = isSettled ? targetRef.current : next;
        applyPosition(indicatorRef.current, currentRef.current);
        if (isSettled) {
          timestampRef.current = null;
          frameRef.current = null;
          return;
        }
        frameRef.current = window.requestAnimationFrame(animate);
      };
      frameRef.current = window.requestAnimationFrame(animate);
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const scheduleFrame = t3;
  let t4;
  if ($[3] !== isActive) {
    t4 = event => {
      if (!isActive || event.pointerType !== "mouse") {
        return;
      }
      const pointerX = getPointerX(event);
      indicatorRef.current?.setAttribute("data-pointer-glow-active", "");
      if (pointerX === null) {
        return;
      }
      targetRef.current = pointerX;
      scheduleFrame();
    };
    $[3] = isActive;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const moveToPointer = t4;
  let t5;
  if ($[5] !== isActive) {
    t5 = event_0 => {
      if (!isActive || event_0.pointerType !== "mouse") {
        return;
      }
      indicatorRef.current?.removeAttribute("data-pointer-glow-active");
      targetRef.current = 0;
      scheduleFrame();
    };
    $[5] = isActive;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const handlePointerLeave = t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      resetMotion();
    };
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== isActive || $[9] !== resetKey) {
    t7 = [isActive, resetKey, resetMotion];
    $[8] = isActive;
    $[9] = resetKey;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => cancelFrame;
    t9 = [cancelFrame];
    $[11] = t8;
    $[12] = t9;
  } else {
    t8 = $[11];
    t9 = $[12];
  }
  useEffect(t8, t9);
  let t10;
  if ($[13] !== handlePointerLeave || $[14] !== moveToPointer) {
    t10 = {
      onPointerEnter: moveToPointer,
      onPointerMove: moveToPointer,
      onPointerLeave: handlePointerLeave
    };
    $[13] = handlePointerLeave;
    $[14] = moveToPointer;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const pointerProps = t10;
  let t11;
  if ($[16] !== isActive || $[17] !== pointerProps) {
    t11 = {
      indicatorRef,
      isActive,
      pointerProps
    };
    $[16] = isActive;
    $[17] = pointerProps;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  return t11;
};